import { useEffect, useRef } from 'react';

function createStalks(w) {
  const count = Math.floor(w / 28);
  return Array.from({ length: count }, (_, i) => ({
    x: (i / count) * w + Math.random() * 20 - 10,
    height: 120 + Math.random() * 180,
    phase: Math.random() * Math.PI * 2,
    speed: 0.3 + Math.random() * 0.4,
    width: 0.8 + Math.random() * 0.7,
    hasGrain: Math.random() > 0.3,
    grainSize: 3 + Math.random() * 4,
  }));
}

function createParticles(count = 30) {
  return Array.from({ length: count }, () => ({
    x: Math.random() * 2000,
    y: Math.random() * 2000,
    size: 1 + Math.random() * 2.5,
    speedX: (Math.random() - 0.5) * 0.3,
    speedY: -0.2 - Math.random() * 0.5,
    opacity: 0.1 + Math.random() * 0.25,
  }));
}

function drawBackground(ctx, w, h) {
  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, '#0D2818');
  grad.addColorStop(0.3, '#142e20');
  grad.addColorStop(0.6, '#1B4332');
  grad.addColorStop(1, '#1a3c30');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

function drawGlows(ctx, w, h) {
  const glow1 = ctx.createRadialGradient(w * 0.3, h * 0.7, 0, w * 0.3, h * 0.7, w * 0.5);
  glow1.addColorStop(0, 'rgba(64, 145, 108, 0.06)');
  glow1.addColorStop(1, 'transparent');
  ctx.fillStyle = glow1;
  ctx.fillRect(0, 0, w, h);

  const glow2 = ctx.createRadialGradient(w * 0.7, h * 0.3, 0, w * 0.7, h * 0.3, w * 0.4);
  glow2.addColorStop(0, 'rgba(176, 125, 59, 0.04)');
  glow2.addColorStop(1, 'transparent');
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, w, h);
}

function drawStalk(ctx, s, t, h) {
  const sway = Math.sin(t * s.speed + s.phase) * 12;
  const sway2 = Math.sin(t * s.speed * 0.7 + s.phase + 1) * 6;
  const baseY = h;
  const tipX = s.x + sway + sway2;
  const tipY = baseY - s.height;
  const midX = s.x + sway * 0.5;
  const midY = baseY - s.height * 0.5;

  ctx.beginPath();
  ctx.moveTo(s.x, baseY);
  ctx.quadraticCurveTo(midX, midY, tipX, tipY);
  ctx.strokeStyle = 'rgba(82, 183, 136, 0.12)';
  ctx.lineWidth = s.width;
  ctx.stroke();

  if (!s.hasGrain) return;

  ctx.beginPath();
  ctx.ellipse(tipX, tipY - 2, s.grainSize * 0.6, s.grainSize, (sway / 12) * 0.3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(196, 146, 58, 0.1)';
  ctx.fill();

  const leafY = baseY - s.height * 0.4;
  const leafX = s.x + sway * 0.35;
  ctx.beginPath();
  ctx.ellipse(leafX - 8, leafY, 3, 10, -0.5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(82, 183, 136, 0.06)';
  ctx.fill();
}

function drawParticle(ctx, p, w) {
  p.x += p.speedX;
  p.y += p.speedY;
  if (p.y < -10) { p.y = 2000; p.x = Math.random() * w; }
  if (p.x < -10 || p.x > w + 10) p.x = Math.random() * w;

  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(82, 183, 136, ${p.opacity})`;
  ctx.fill();
}

export function useWheatCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let w, h, stalks, particles, time = 0, raf;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      stalks = createStalks(w);
    };

    particles = createParticles();

    const animate = () => {
      time += 0.015;
      ctx.clearRect(0, 0, w, h);
      drawBackground(ctx, w, h);
      drawGlows(ctx, w, h);
      stalks.forEach((s) => drawStalk(ctx, s, time, h));
      particles.forEach((p) => drawParticle(ctx, p, w));
      raf = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return canvasRef;
}
